import { useDraggable } from '@dnd-kit/core';
import { useAtomValue } from 'jotai';
import type { Ref } from 'react';
import {
    ArrowUpRightIcon,
    BodyMediumRegular,
    BodySmallRegular,
    CarFrontIcon,
    CirclePlusIcon,
    GiftIcon,
    GraduationCapIcon,
    GripVerticalIcon,
    H6Emphasized,
    HomeIcon,
    Icon,
    IconButton,
    type IconType,
    LayersIcon,
    LuggageIcon,
    ReceiptTextIcon,
    SmoothieScrollView,
    TreePalmIcon,
    UmbrellaIcon,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { BACKDOOR_ROTH_ENABLED_IN_GROVE } from '../../../config/FruitfulEnvironment';
import { selectedMembershipAtom } from '../../../entities/entityAtoms';
import type { MembershipDetails } from '../../../entities/entityTypes';
import {
    getGoalPanelPresetLabel,
    getGoalPanelPresetRuleCount,
    GoalPanelPreset,
    GoalPanelPresetDescription,
    isJointMembershipWithPartner,
} from '../../utils/buildGoalPanelPresetGroupDto';

interface GoalPresetsPanelProps {
    onClose: () => void;
}

const PRESET_ICON_MAP: Record<GoalPanelPreset, IconType> = {
    [GoalPanelPreset.NEW_GOAL]: CirclePlusIcon,
    [GoalPanelPreset.ONE_MONTH_EMERGENCY_FUND]: UmbrellaIcon,
    [GoalPanelPreset.FULL_EMERGENCY_FUND]: UmbrellaIcon,
    [GoalPanelPreset.FINANCIAL_HEALTH]: LayersIcon,
    [GoalPanelPreset.INVESTMENT_FUND]: TreePalmIcon,
    [GoalPanelPreset.BACKDOOR_ROTH]: ArrowUpRightIcon,
    [GoalPanelPreset.BACKDOOR_ROTH_PARTNER]: ArrowUpRightIcon,
    [GoalPanelPreset.PAY_OFF_DEBT]: ReceiptTextIcon,
    [GoalPanelPreset.DOWN_PAYMENT]: HomeIcon,
    [GoalPanelPreset.EDUCATION_FUND]: GraduationCapIcon,
    [GoalPanelPreset.BUY_A_CAR]: CarFrontIcon,
    [GoalPanelPreset.TRAVEL_FUND]: LuggageIcon,
    [GoalPanelPreset.GIFTS_FUND]: GiftIcon,
};

const BASE_PRESETS: GoalPanelPreset[] = [
    GoalPanelPreset.NEW_GOAL,
    GoalPanelPreset.ONE_MONTH_EMERGENCY_FUND,
    GoalPanelPreset.FULL_EMERGENCY_FUND,
    GoalPanelPreset.FINANCIAL_HEALTH,
    GoalPanelPreset.INVESTMENT_FUND,
    GoalPanelPreset.PAY_OFF_DEBT,
    GoalPanelPreset.DOWN_PAYMENT,
    GoalPanelPreset.EDUCATION_FUND,
    GoalPanelPreset.BUY_A_CAR,
    GoalPanelPreset.TRAVEL_FUND,
    GoalPanelPreset.GIFTS_FUND,
];

/**
 * Inserts the Backdoor Roth preset(s) right after INVESTMENT_FUND when the env flag is enabled.
 * Joint memberships with a partner also get a second, partner-owned Backdoor Roth tile.
 * Mirrors the gating in the old goal preset selection (`GoalPresetSelection.tsx`).
 */
const getVisiblePresets = (isJointWithPartner: boolean): GoalPanelPreset[] => {
    if (!BACKDOOR_ROTH_ENABLED_IN_GROVE) {
        return BASE_PRESETS;
    }
    const insertAfter = BASE_PRESETS.indexOf(GoalPanelPreset.INVESTMENT_FUND);
    const backdoorPresets = isJointWithPartner
        ? [GoalPanelPreset.BACKDOOR_ROTH, GoalPanelPreset.BACKDOOR_ROTH_PARTNER]
        : [GoalPanelPreset.BACKDOOR_ROTH];
    return [
        ...BASE_PRESETS.slice(0, insertAfter + 1),
        ...backdoorPresets,
        ...BASE_PRESETS.slice(insertAfter + 1),
    ];
};

const PRESET_TILE_WIDTH = 176;
const PRESET_TILE_HEIGHT = 192;
const TILE_GAP = 8;
const TILE_PADDING_HORIZONTAL = 18;
const TILES_PER_ROW = 3;

// Exported so the parent can size the animated panel container to fit exactly.
export const GOAL_PRESET_PANEL_WIDTH =
    TILES_PER_ROW * PRESET_TILE_WIDTH +
    (TILES_PER_ROW - 1) * TILE_GAP +
    TILE_PADDING_HORIZONTAL * 2;

interface PresetTileProps {
    preset: GoalPanelPreset;
    membership: MembershipDetails | null | undefined;
}

const PresetTile = ({ preset, membership }: PresetTileProps) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: preset,
        data: { preset, presetRuleCount: getGoalPanelPresetRuleCount(preset) },
    });

    return (
        <YStack
            ref={setNodeRef as unknown as Ref<HTMLElement>}
            flexShrink={0}
            {...listeners}
            {...(attributes as unknown as Record<string, unknown>)}
            style={{
                touchAction: 'none',
                userSelect: 'none',
                cursor: isDragging ? 'grabbing' : 'grab',
                outline: 'none',
            }}
        >
            <YStack
                width={PRESET_TILE_WIDTH}
                height={PRESET_TILE_HEIGHT}
                backgroundColor="$gray100"
                borderRadius={24}
                padding={16}
                gap={24}
                animation="quick"
                hoverStyle={{ backgroundColor: '$gray200' }}
                style={{ opacity: isDragging ? 0 : 1 }}
            >
                <Icon icon={PRESET_ICON_MAP[preset]} size={20} />
                <YStack gap={4} overflow="hidden">
                    <BodyMediumRegular color="$gray1200">
                        {getGoalPanelPresetLabel(preset, membership)}
                    </BodyMediumRegular>
                    <BodySmallRegular color="$gray1000">
                        {GoalPanelPresetDescription[preset]}
                    </BodySmallRegular>
                </YStack>
            </YStack>
        </YStack>
    );
};

/** Floating card rendered inside DragOverlay while a preset tile is being dragged. */
export const PresetTileDragOverlay = ({
    preset,
}: {
    preset: GoalPanelPreset;
}) => {
    const membership = useAtomValue(selectedMembershipAtom);
    return (
        <YStack
            width={PRESET_TILE_WIDTH}
            height={PRESET_TILE_HEIGHT}
            backgroundColor="$gray100"
            borderRadius={24}
            padding={16}
            style={{
                boxShadow:
                    '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
                cursor: 'grabbing',
            }}
        >
            <XStack justifyContent="space-between" alignItems="flex-start">
                <Icon icon={PRESET_ICON_MAP[preset]} size={20} />
                <Icon icon={GripVerticalIcon} size={16} color="$gray1100" />
            </XStack>
            <YStack gap={4} overflow="hidden">
                <BodyMediumRegular color="$gray1200">
                    {getGoalPanelPresetLabel(preset, membership)}
                </BodyMediumRegular>
                <BodySmallRegular color="$gray1000">
                    {GoalPanelPresetDescription[preset]}
                </BodySmallRegular>
            </YStack>
        </YStack>
    );
};

export const GoalPresetsPanel = ({ onClose }: GoalPresetsPanelProps) => {
    const membership = useAtomValue(selectedMembershipAtom);
    const visiblePresets = getVisiblePresets(
        isJointMembershipWithPartner(membership),
    );
    const presetRows: GoalPanelPreset[][] = [];
    for (let i = 0; i < visiblePresets.length; i += TILES_PER_ROW) {
        presetRows.push(visiblePresets.slice(i, i + TILES_PER_ROW));
    }

    return (
        <YStack
            flex={1}
            height="100%"
            backgroundColor="$white"
            overflow="hidden"
        >
            <XStack
                alignItems="center"
                justifyContent="space-between"
                paddingHorizontal={18}
                paddingTop={20}
                paddingBottom={16}
            >
                <H6Emphasized>Add goals</H6Emphasized>
                <IconButton
                    variant="secondary"
                    size="tiny"
                    icon={XIcon}
                    onPress={onClose}
                />
            </XStack>

            <SmoothieScrollView>
                <YStack
                    gap={TILE_GAP}
                    paddingHorizontal={TILE_PADDING_HORIZONTAL}
                    paddingBottom={20}
                >
                    {presetRows.map((row, rowIndex) => (
                        <XStack key={rowIndex} gap={TILE_GAP}>
                            {row.map((preset) => (
                                <PresetTile
                                    key={preset}
                                    preset={preset}
                                    membership={membership}
                                />
                            ))}
                        </XStack>
                    ))}
                </YStack>
            </SmoothieScrollView>
        </YStack>
    );
};
